import type { Address } from './address';
import { type APIClient, type APIAddressSummary, type APIHistoryItem, APIError, isUsed } from './api';
import { equal, hex } from './bytes';
import { outPoint } from './tx';
import type { Coin } from './txbuilder';
import { CHANGE_CHAIN, type HDWallet, RECEIVE_CHAIN } from './wallet';

export interface WalletAddress {
  chain: number;
  index: number;
  address: Address;
}

/** Everything the home screen needs, computed from the API. */
export interface WalletSnapshot {
  height: number;
  confirmed: number;
  unconfirmed: number;
  total: number;
  usedAddresses: WalletAddress[];
  /** First unused receive address: the one shown on "Receive". */
  receiveAddress: WalletAddress;
  nextChangeIndex: number;
  /** Spendable coins (immature coinbase excluded). */
  coins: Coin[];
  history: APIHistoryItem[];
  historyCursor: string | null;
}

export class InconsistentServerData extends Error {
  constructor() {
    super('inconsistent server data');
  }
}

/** Discovers used addresses (BIP44 gap limit) and loads balance, coins and history. */
export class WalletSync {
  constructor(
    readonly wallet: HDWallet,
    readonly api: APIClient,
    readonly gapLimit = 20,
  ) {}

  private async scan(chain: number) {
    const used: [WalletAddress, APIAddressSummary][] = [];
    let lastUsed = -1;
    let start = 0;
    while (start <= lastUsed + this.gapLimit) {
      const batch: WalletAddress[] = [];
      for (let i = start; i < start + this.gapLimit; i++) {
        batch.push({ chain, index: i, address: this.wallet.address(chain, i) });
      }
      const summary = await this.api.summary(batch.map((w) => w.address.string));
      const by = new Map(summary.addresses.map((a) => [a.address, a]));
      for (const wa of batch) {
        const s = by.get(wa.address.string);
        if (!s) throw new APIError({ kind: 'invalidResponse' });
        if (isUsed(s)) {
          used.push([wa, s]);
          lastUsed = wa.index;
        }
      }
      start += this.gapLimit;
    }
    return { used, lastUsed };
  }

  async refresh(historyLimit = 25): Promise<WalletSnapshot> {
    const [receive, change] = await Promise.all([this.scan(RECEIVE_CHAIN), this.scan(CHANGE_CHAIN)]);
    const used = [...receive.used, ...change.used];
    const confirmed = used.reduce((n, [, s]) => n + s.confirmed, 0);
    const unconfirmed = used.reduce((n, [, s]) => n + s.unconfirmed, 0);
    const usedAddresses = used.map(([w]) => w);
    const byString = new Map(usedAddresses.map((w) => [w.address.string, w]));

    const coins: Coin[] = [];
    for (let i = 0; i < usedAddresses.length; i += 200) {
      const chunk = usedAddresses.slice(i, i + 200);
      for (const u of await this.api.utxos(chunk.map((w) => w.address.string))) {
        if (!u.spendable) continue;
        const wa = byString.get(u.address);
        const script = hex.decode(u.script_pubkey);
        const op = outPoint(u.txid, u.vout);
        if (!wa || !script || !equal(script, wa.address.scriptPubKey) || !op || !(u.value > 0)) {
          throw new InconsistentServerData();
        }
        coins.push({ outpoint: op, value: u.value, scriptPubKey: script, chain: wa.chain, index: wa.index, confirmations: u.confirmations });
      }
    }

    let history: APIHistoryItem[] = [];
    let cursor: string | null = null;
    let height = 0;
    if (usedAddresses.length > 0) {
      const page = await this.api.history(usedAddresses.slice(-200).map((w) => w.address.string), null, historyLimit);
      history = page.txs;
      cursor = page.next_cursor;
      height = page.height;
    }

    const nextReceive = receive.lastUsed + 1;
    return {
      height,
      confirmed,
      unconfirmed,
      total: confirmed + unconfirmed,
      usedAddresses,
      receiveAddress: { chain: RECEIVE_CHAIN, index: nextReceive, address: this.wallet.receiveAddress(nextReceive) },
      nextChangeIndex: change.lastUsed + 1,
      coins,
      history,
      historyCursor: cursor,
    };
  }
}
